/**
 * Prefetch Store — Background data preloading for instant tab switching.
 *
 * On first auth + projectID, all main API calls are fired in parallel.
 * Each view checks the cache first and renders immediately if data exists,
 * then silently refreshes in the background to pick up changes.
 */
import { defineStore } from 'pinia';
import { getProject } from '@/api/project';
import { listCustomers } from '@/api/customers';
import { getLeadForms } from '@/api/forms';
import { getTopReferences } from '@/api/projects';
import { listTemplates } from '@/api/templates';

interface CacheEntry<T> {
  data: T;
  fetchedAt: number;
  projectId: string;
  sizeKB: number;
}

/** Max age (ms) before a cache entry is considered stale and must be refreshed */
const STALE_MS = 60_000; // 1 minute

const PREFIX = '%c[prefetch]';
const STYLE = 'color:#8b5cf6;font-weight:bold';
const STYLE_DIM = 'color:#8b5cf6';
const STYLE_WARN = 'color:#f59e0b;font-weight:bold';
const STYLE_OK = 'color:#22c55e;font-weight:bold';

/** Rough estimate of JSON-serialized size in KB */
function estimateSizeKB(obj: unknown): number {
  try {
    const str = JSON.stringify(obj);
    return str ? Math.round((str.length * 2) / 1024) : 0;
  } catch {
    return 0;
  }
}

// Module-level promise for awaiting in-flight prefetch
let _prefetchPromise: Promise<void> | null = null;

export const usePrefetchStore = defineStore('prefetch', {
  state: () => ({
    project: null as CacheEntry<unknown> | null,
    projectWithCustomers: null as CacheEntry<unknown> | null,
    customers: null as CacheEntry<unknown> | null,
    forms: null as CacheEntry<unknown> | null,
    topReferences: null as CacheEntry<unknown> | null,
    templates: null as CacheEntry<unknown> | null,
    _inflight: new Set<string>(),
    _prefetched: false
  }),

  getters: {
    /** Check if a cache entry is still fresh */
    isFresh:
      () =>
      <T>(entry: CacheEntry<T> | null, projectId: string): boolean => {
        if (!entry) return false;
        if (entry.projectId !== projectId) return false;
        return Date.now() - entry.fetchedAt < STALE_MS;
      },

    /** Get cached data if it exists for this project (even if stale) */
    getCached:
      () =>
      <T>(entry: CacheEntry<T> | null, projectId: string): T | null => {
        if (!entry || entry.projectId !== projectId) {
          console.log(PREFIX, STYLE_DIM, `MISS (no data)`);
          return null;
        }
        const age = ((Date.now() - entry.fetchedAt) / 1000).toFixed(1);
        console.log(PREFIX, STYLE_OK, `HIT — ${entry.sizeKB}KB, ${age}s old`);
        return entry.data;
      },

    /** Total memory used by all cache entries */
    totalCacheKB(): number {
      let total = 0;
      const keys = [
        'project',
        'projectWithCustomers',
        'customers',
        'forms',
        'topReferences',
        'templates'
      ] as const;
      for (const k of keys) {
        const entry = this[k];
        if (entry) total += entry.sizeKB;
      }
      return total;
    }
  },

  actions: {
    /** Log a summary table of cache state */
    _logSummary() {
      const keys = [
        'project',
        'projectWithCustomers',
        'customers',
        'forms',
        'topReferences',
        'templates'
      ] as const;
      const rows: Record<
        string,
        { sizeKB: number; ageSeconds: string; status: string }
      > = {};
      for (const k of keys) {
        const entry = this[k];
        if (entry) {
          const age = ((Date.now() - entry.fetchedAt) / 1000).toFixed(1);
          rows[k] = {
            sizeKB: entry.sizeKB,
            ageSeconds: age,
            status: '✅ cached'
          };
        } else {
          rows[k] = { sizeKB: 0, ageSeconds: '-', status: '⬜ empty' };
        }
      }
      console.log(PREFIX, STYLE, 'Cache summary:');
      console.table(rows);
      const total = this.totalCacheKB;
      const warnStyle = total > 5000 ? STYLE_WARN : STYLE_OK;
      console.log(
        PREFIX,
        warnStyle,
        `Total cache: ${total}KB (~${(total / 1024).toFixed(1)}MB)` +
          (total > 5000 ? ' ⚠️ Consider reducing cache size' : ' ✓ OK')
      );
    },

    /**
     * Prefetch all data for a project in the background.
     * Safe to call multiple times — deduplicates in-flight requests.
     */
    async prefetchAll(projectId: string) {
      if (!projectId || projectId === 'undefined' || projectId === 'default')
        return;
      if (this._prefetched && this.project?.projectId === projectId) {
        console.log(
          PREFIX,
          STYLE_DIM,
          `Already prefetched for ${projectId}, skipping`
        );
        return;
      }
      this._prefetched = true;

      const t0 = performance.now();
      console.log(PREFIX, STYLE, `Starting prefetch for ${projectId}…`);

      // Fire all fetches in parallel — each is independently caught
      const tasks = [
        this._fetchProject(projectId),
        this._fetchProjectWithCustomers(projectId),
        this._fetchCustomers(projectId),
        this._fetchForms(projectId),
        this._fetchTopReferences(projectId),
        this._fetchTemplates(projectId)
      ];

      _prefetchPromise = Promise.allSettled(tasks).then(() => {
        const elapsed = (performance.now() - t0).toFixed(0);
        console.log(PREFIX, STYLE_OK, `All fetches done in ${elapsed}ms`);
        this._logSummary();
        _prefetchPromise = null;
      });
      await _prefetchPromise;
    },

    /**
     * Wait for any in-flight prefetch to complete.
     * Views call this instead of making their own duplicate API requests.
     */
    async waitForPrefetch() {
      if (_prefetchPromise) {
        console.log(PREFIX, STYLE_DIM, 'Waiting for in-flight prefetch…');
        await _prefetchPromise;
      }
    },

    /**
     * Silently refresh a specific cache key. Views call this on mount
     * so the user sees cached data instantly but gets fresh data shortly after.
     */
    async refresh(
      key:
        | 'project'
        | 'projectWithCustomers'
        | 'customers'
        | 'forms'
        | 'topReferences'
        | 'templates',
      projectId: string
    ) {
      switch (key) {
        case 'project':
          return this._fetchProject(projectId);
        case 'projectWithCustomers':
          return this._fetchProjectWithCustomers(projectId);
        case 'customers':
          return this._fetchCustomers(projectId);
        case 'forms':
          return this._fetchForms(projectId);
        case 'topReferences':
          return this._fetchTopReferences(projectId);
        case 'templates':
          return this._fetchTemplates(projectId);
      }
    },

    /** Clear cache (e.g., on sign-out or project switch) */
    clearAll() {
      console.log(PREFIX, STYLE, 'Cache cleared');
      this.project = null;
      this.projectWithCustomers = null;
      this.customers = null;
      this.forms = null;
      this.topReferences = null;
      this.templates = null;
      this._prefetched = false;
      this._inflight.clear();
    },

    // ── Internal fetchers (deduplicated) ─────────────────────────

    async _fetchProject(projectId: string) {
      const key = 'project';
      if (this._inflight.has(key)) return;
      this._inflight.add(key);
      const t0 = performance.now();
      try {
        const data = await getProject(projectId, false);
        if (data) {
          const sizeKB = estimateSizeKB(data);
          this.project = { data, fetchedAt: Date.now(), projectId, sizeKB };
          console.log(
            PREFIX,
            STYLE_DIM,
            `project: ${(performance.now() - t0).toFixed(0)}ms, ${sizeKB}KB`
          );
        }
      } catch (e) {
        console.warn('[prefetch] project fetch failed:', e);
      } finally {
        this._inflight.delete(key);
      }
    },

    async _fetchProjectWithCustomers(projectId: string) {
      const key = 'projectWithCustomers';
      if (this._inflight.has(key)) return;
      this._inflight.add(key);
      const t0 = performance.now();
      try {
        const data = await getProject(projectId, true);
        if (data) {
          const sizeKB = estimateSizeKB(data);
          this.projectWithCustomers = {
            data,
            fetchedAt: Date.now(),
            projectId,
            sizeKB
          };
          console.log(
            PREFIX,
            STYLE_DIM,
            `projectWithCustomers: ${(performance.now() - t0).toFixed(
              0
            )}ms, ${sizeKB}KB`
          );
        }
      } catch (e) {
        console.warn('[prefetch] projectWithCustomers fetch failed:', e);
      } finally {
        this._inflight.delete(key);
      }
    },

    async _fetchCustomers(projectId: string) {
      const key = 'customers';
      if (this._inflight.has(key)) return;
      this._inflight.add(key);
      const t0 = performance.now();
      try {
        const data = await listCustomers(
          projectId,
          100,
          undefined,
          'lastUpdate',
          'desc'
        );
        if (data) {
          const sizeKB = estimateSizeKB(data);
          this.customers = { data, fetchedAt: Date.now(), projectId, sizeKB };
          console.log(
            PREFIX,
            STYLE_DIM,
            `customers: ${(performance.now() - t0).toFixed(0)}ms, ${sizeKB}KB`
          );
        }
      } catch (e) {
        console.warn('[prefetch] customers fetch failed:', e);
      } finally {
        this._inflight.delete(key);
      }
    },

    async _fetchForms(projectId: string) {
      const key = 'forms';
      if (this._inflight.has(key)) return;
      this._inflight.add(key);
      const t0 = performance.now();
      try {
        const data = await getLeadForms(projectId);
        if (data) {
          const sizeKB = estimateSizeKB(data);
          this.forms = { data, fetchedAt: Date.now(), projectId, sizeKB };
          console.log(
            PREFIX,
            STYLE_DIM,
            `forms: ${(performance.now() - t0).toFixed(0)}ms, ${sizeKB}KB`
          );
        }
      } catch (e) {
        console.warn('[prefetch] forms fetch failed:', e);
      } finally {
        this._inflight.delete(key);
      }
    },

    async _fetchTopReferences(projectId: string) {
      const key = 'topReferences';
      if (this._inflight.has(key)) return;
      this._inflight.add(key);
      const t0 = performance.now();
      try {
        const data = await getTopReferences(projectId);
        if (data) {
          const sizeKB = estimateSizeKB(data);
          this.topReferences = {
            data,
            fetchedAt: Date.now(),
            projectId,
            sizeKB
          };
          console.log(
            PREFIX,
            STYLE_DIM,
            `topReferences: ${(performance.now() - t0).toFixed(
              0
            )}ms, ${sizeKB}KB`
          );
        }
      } catch (e) {
        console.warn('[prefetch] topReferences fetch failed:', e);
      } finally {
        this._inflight.delete(key);
      }
    },

    async _fetchTemplates(projectId: string) {
      const key = 'templates';
      if (this._inflight.has(key)) return;
      this._inflight.add(key);
      const t0 = performance.now();
      try {
        const data = await listTemplates(projectId);
        if (data) {
          const sizeKB = estimateSizeKB(data);
          this.templates = { data, fetchedAt: Date.now(), projectId, sizeKB };
          console.log(
            PREFIX,
            STYLE_DIM,
            `templates: ${(performance.now() - t0).toFixed(0)}ms, ${sizeKB}KB`
          );
        }
      } catch (e) {
        console.warn('[prefetch] templates fetch failed:', e);
      } finally {
        this._inflight.delete(key);
      }
    }
  }
});
